import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as dashboardApi from '../../../api/dashboardApi';
import * as adminApi from '../../../api/adminApi';
import { User as AuthUser } from '../../../api/authApi';
import { useAuth } from '../../../context/AuthContext';

interface AdminDataState {
  stats: adminApi.AdminDashboardStats | null;
  documents: adminApi.AdminDocument[];
  transcripts: adminApi.AdminYoutubeTranscript[];
  users: adminApi.AdminUserSummary[];
  admins: adminApi.AdminSummary[];
  dashboardData: {
    regulations: dashboardApi.RegulationSummary[];
    timeline: dashboardApi.TimelineItem[];
    trends: dashboardApi.TrendDataPoint[];
    jurisdictions: dashboardApi.JurisdictionStats[];
  };
  dashboardLoading: boolean;
  loading: boolean;
  secondaryLoading: boolean;
  error: string | null;
  adminProfile: AuthUser | null;
}

export const useAdminData = () => {
  const navigate = useNavigate();
  // Use a ref so fetch callbacks don't depend on navigate directly,
  // preventing them from being recreated on every render.
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  const { user: authUser } = useAuth();
  const [state, setState] = useState<AdminDataState>({
    stats: null,
    documents: [],
    transcripts: [],
    users: [],
    admins: [],
    dashboardData: {
      regulations: [],
      timeline: [],
      trends: [],
      jurisdictions: [],
    },
    dashboardLoading: true,
    loading: true,
    secondaryLoading: true,
    error: null,
    adminProfile: null,
  });

  // Read token inline — never captured in deps, so callbacks are always stable.
  const requireToken = useCallback(() => {
    const t = window.localStorage.getItem('policyora_token');
    if (!t) {
      navigateRef.current('/admin/login');
      return null;
    }
    return t;
  }, []); // stable — reads via ref + inline localStorage

  // Empty deps → reference never changes → useEffect runs exactly once.
  const fetchAdminData = useCallback(async () => {
    const authToken = requireToken();
    if (!authToken) return;

    // Core stats — the ONLY hard requirement to render the dashboard.
    try {
      const stats = await adminApi.getAdminDashboardStats(authToken);
      setState(prev => ({ ...prev, stats, loading: false }));
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        navigateRef.current('/admin/login');
        return;
      }
      setState(prev => ({ ...prev, error: 'Failed to load admin data', loading: false, secondaryLoading: false }));
      console.error('Admin data error (stats):', err);
      return;
    }

    // Everything below is best-effort. A transient failure of any ONE call must
    // NOT blank the whole dashboard — previously a single Promise.all rejection
    // surfaced "Failed to load admin data" for the entire page.
    try {
      const admins = await adminApi.getAdmins(authToken);
      setState(prev => ({ ...prev, admins, secondaryLoading: false }));
    } catch (err) {
      if ((err as { status?: number }).status === 401) { navigateRef.current('/admin/login'); return; }
      setState(prev => ({ ...prev, secondaryLoading: false }));
      console.error('Admin secondary load failed (admins):', err);
    }

    const [documents, transcripts, users] = await Promise.allSettled([
      adminApi.getAdminDocuments(authToken),
      adminApi.getAdminYoutubeTranscripts(authToken),
      adminApi.getAdminUsers(authToken),
    ]);
    if (documents.status === 'rejected' || transcripts.status === 'rejected' || users.status === 'rejected') {
      console.error('Admin secondary load partial failure:', {
        documents: documents.status, transcripts: transcripts.status, users: users.status,
      });
    }
    setState(prev => ({
      ...prev,
      documents: documents.status === 'fulfilled' ? documents.value : prev.documents,
      transcripts: transcripts.status === 'fulfilled' ? transcripts.value : prev.transcripts,
      users: users.status === 'fulfilled' ? users.value : prev.users,
    }));
  }, [requireToken]);

  const fetchDashboardWidgets = useCallback(async () => {
    const authToken = requireToken();
    if (!authToken) return;

    setState(prev => ({ ...prev, dashboardLoading: true }));
    try {
      const [recentRegs, timeline, trends, stats] = await Promise.all([
        dashboardApi.getRecentRegulations({ days: 30 }),
        dashboardApi.getTimeline({ limit: 20, date_type: 'both' }),
        dashboardApi.getTrends({ granularity: 'month' }),
        dashboardApi.getJurisdictionStats({}),
      ]);
      setState(prev => ({
        ...prev,
        dashboardData: {
          regulations: recentRegs.regulations,
          timeline: timeline.items,
          trends: trends.data,
          jurisdictions: stats.stats,
        },
        dashboardLoading: false,
      }));
    } catch (err) {
      console.error('Dashboard widgets error:', err);
      setState(prev => ({ ...prev, dashboardLoading: false }));
    }
  }, [requireToken]);

  const refreshAdminData = useCallback(() => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    fetchAdminData();
    fetchDashboardWidgets();
  }, [fetchAdminData, fetchDashboardWidgets]);

  const addDocument = useCallback((document: adminApi.AdminDocument) => {
    setState(prev => ({ ...prev, documents: [document, ...prev.documents] }));
  }, []);

  const addTranscript = useCallback((transcript: adminApi.AdminYoutubeTranscript) => {
    setState(prev => ({ ...prev, transcripts: [transcript, ...prev.transcripts] }));
  }, []);

  const updateProfile = useCallback((profile: AuthUser) => {
    setState(prev => ({ ...prev, adminProfile: profile }));
  }, []);

  // Sync adminProfile from AuthContext
  useEffect(() => {
    if (authUser) setState(prev => ({ ...prev, adminProfile: authUser as AuthUser }));
  }, [authUser]);

  // Runs exactly once on mount — fetchAdminData and fetchDashboardWidgets are stable.
  useEffect(() => {
    fetchAdminData();
    fetchDashboardWidgets();
  }, [fetchAdminData, fetchDashboardWidgets]);

  return {
    ...state,
    requireToken,
    refreshAdminData,
    addDocument,
    addTranscript,
    updateProfile,
  };
};
